import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { rewardsService } from '../../services/rewardsService';
import { referralService } from '../../services/referralService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import PointsOverviewCard from './components/PointsOverviewCard';
import EarningOpportunityCard from './components/EarningOpportunityCard';
import RedemptionCard from './components/RedemptionCard';
import PointHistoryItem from './components/PointHistoryItem';
import RedeemModal from './components/RedeemModal';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';


const RewardsCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasProAccess } = useSubscription();
  const [userPoints, setUserPoints] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [redemptionOptions, setRedemptionOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [activeTab, setActiveTab] = useState('rewards'); // 'earn', 'redeem', 'history'

  // Referral state
  const [referralLink, setReferralLink] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referralSignups, setReferralSignups] = useState([]);
  const [bonusStructure, setBonusStructure] = useState([]);
  const [milestoneBonuses, setMilestoneBonuses] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadRewardsData();
      loadReferralData();
    }
  }, [user?.id]);

  const loadRewardsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [pointsData, transactionsData, redemptionsData] = await Promise.all([
        rewardsService?.getUserPoints(user?.id),
        rewardsService?.getPointTransactions(user?.id, 20),
        rewardsService?.getRedemptionOptions()
      ]);
      
      setUserPoints(pointsData);
      setTransactions(transactionsData);
      setRedemptionOptions(redemptionsData);
    } catch (err) {
      setError(err?.message || 'Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  };

  const loadReferralData = async () => {
    try {
      const [linkData, statsData, signupsData] = await Promise.all([
        referralService?.getReferralLink(user?.id),
        referralService?.getReferralStats(user?.id),
        referralService?.getReferralSignups(user?.id)
      ]);

      setReferralLink(linkData);
      setReferralStats(statsData);
      setReferralSignups(signupsData);
      setBonusStructure(referralService?.getReferralBonusStructure());
      setMilestoneBonuses(referralService?.getMilestoneBonuses());
    } catch (err) {
      console.error('Error loading referral data:', err);
    }
  };

  const handleRedeemClick = (redemption) => {
    if (redemption?.requiresPro && !hasProAccess) {
      alert('This reward requires a Pro subscription');
      return;
    }
    
    if (userPoints?.totalPoints < redemption?.pointCost) {
      alert('Insufficient points for this reward');
      return;
    }
    
    setSelectedRedemption(redemption);
    setShowRedeemModal(true);
  };

  const handleConfirmRedeem = async () => {
    try {
      await rewardsService?.redeemReward(user?.id, selectedRedemption?.id);
      setShowRedeemModal(false);
      setSelectedRedemption(null);
      await loadRewardsData();
      alert('Reward redeemed successfully!');
    } catch (err) {
      alert(err?.message || 'Failed to redeem reward');
    }
  };

  const handleCopyLink = async () => {
    if (referralLink?.referralCode) {
      const success = await referralService?.copyReferralLink(referralLink?.referralCode);
      if (success) {
        alert('Referral link copied to clipboard!');
      } else {
        alert('Failed to copy link. Please try again.');
      }
    }
  };

  const handleShare = (platform) => {
    if (!referralLink?.referralCode) return;

    const url = referralService?.generateReferralUrl(referralLink?.referralCode);
    const message = `Join me on Rovyn and start managing your finances better! Use my referral link: ${url}`;

    let shareUrl = '';
    switch (platform) {
      case 'email':
        shareUrl = `mailto:?subject=Join me on Rovyn&body=${encodeURIComponent(message)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const tierProgress = userPoints ? rewardsService?.getTierProgress(userPoints?.lifetimeEarned) : null;
  const earningOpportunities = rewardsService?.getEarningOpportunities();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Rewards Center', path: '/rewards-center' }
  ];

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Rewards Center - ROVYN</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-16">
            <div className="mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-48 max-w-[1400px]">
              <div className="flex items-center justify-center py-64">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-16"></div>
                  <p className="text-muted-foreground">Loading rewards...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Rewards Center - ROVYN</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-48 max-w-[1400px]">
            <Breadcrumbs items={breadcrumbItems} />
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-16 mb-32">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-secondary mb-8">
                  Rewards Center
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Earn points for good financial habits and redeem them for exclusive benefits
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-16 mb-24">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Points Overview */}
            <div className="mb-32">
              <PointsOverviewCard 
                userPoints={userPoints}
                tierProgress={tierProgress}
                hasProAccess={hasProAccess}
              />
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-8 mb-24 border-b border-border">
              <button
                onClick={() => setActiveTab('rewards')}
                className={`px-16 py-12 font-medium transition-smooth border-b-2 ${
                  activeTab === 'rewards' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="TrendingUp" size={16} className="inline mr-8" />
                Earn & Redeem
              </button>
              <button
                onClick={() => setActiveTab('referrals')}
                className={`px-16 py-12 font-medium transition-smooth border-b-2 ${
                  activeTab === 'referrals' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Users" size={16} className="inline mr-8" />
                Referral Program
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'rewards' && (
              <>
                {/* Earning Opportunities */}
                <div className="mb-24">
                  <h2 className="text-2xl font-heading font-semibold text-secondary mb-12">
                    Ways to Earn Points
                  </h2>
                  <p className="text-muted-foreground">
                    Complete these activities to earn points and level up your tier
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
                    {earningOpportunities?.map((opportunity) => (
                      <EarningOpportunityCard
                        key={opportunity?.id}
                        opportunity={opportunity}
                      />
                    ))}
                  </div>
                </div>

                {/* Redemption Marketplace */}
                <div className="mb-24">
                  <h2 className="text-2xl font-heading font-semibold text-secondary mb-12">
                    Redemption Marketplace
                  </h2>
                  <p className="text-muted-foreground">
                    Use your points to unlock exclusive rewards and benefits
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20">
                    {redemptionOptions?.map((redemption) => (
                      <RedemptionCard
                        key={redemption?.id}
                        redemption={redemption}
                        userPoints={userPoints?.totalPoints}
                        hasProAccess={hasProAccess}
                        onRedeem={() => handleRedeemClick(redemption)}
                      />
                    ))}
                  </div>
                </div>

                {/* Point History */}
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-secondary mb-12">
                    Point History
                  </h2>
                  <p className="text-muted-foreground">
                    Track all your point earnings and redemptions
                  </p>
                  <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden">
                    {transactions?.length === 0 ? (
                      <div className="p-48 text-center">
                        <Icon name="History" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                        <p className="text-muted-foreground">No transaction history yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {transactions?.map((transaction) => (
                          <PointHistoryItem
                            key={transaction?.id}
                            transaction={transaction}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'referrals' && (
              <div className="space-y-32">
                {/* Referral Overview */}
                <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 rounded-lg p-24 md:p-32 shadow-elevation-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-20">
                    {[
                      {
                        label: 'Total Friends Referred',
                        value: referralStats?.totalFriendsReferred || 0,
                        icon: 'Users',
                        color: 'var(--color-primary)'
                      },
                      {
                        label: 'Pending Signups',
                        value: referralStats?.pendingSignups || 0,
                        icon: 'Clock',
                        color: 'var(--color-warning)'
                      },
                      {
                        label: 'Successful Conversions',
                        value: referralStats?.successfulConversions || 0,
                        icon: 'CheckCircle',
                        color: 'var(--color-success)'
                      },
                      {
                        label: 'Total Bonus Points',
                        value: referralStats?.totalBonusPoints?.toLocaleString() || 0,
                        icon: 'Coins',
                        color: 'var(--color-accent)'
                      }
                    ]?.map((item, index) => (
                      <div key={index} className="bg-card/50 backdrop-blur-sm rounded-lg p-20">
                        <div className="flex items-center gap-12 mb-12">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg" style={{ backgroundColor: `${item?.color}15` }}>
                            <Icon name={item?.icon} size={20} color={item?.color} strokeWidth={2.5} />
                          </div>
                          <span className="text-xs font-caption font-medium text-muted-foreground uppercase tracking-wide">
                            {item?.label}
                          </span>
                        </div>
                        <p className="text-3xl font-heading font-bold text-card-foreground">{item?.value}</p>
                      </div>
                    ))}
                  </div>

                  {referralStats?.totalFriendsReferred > 0 && (
                    <div className="mt-24 pt-24 border-t border-border/50">
                      <div className="flex items-center justify-between mb-12">
                        <div className="flex items-center gap-8">
                          <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                          <span className="text-sm font-medium text-muted-foreground">Conversion Rate</span>
                        </div>
                        <span className="text-lg font-heading font-bold text-success">{referralStats?.conversionRate}%</span>
                      </div>
                      <div className="w-full h-8 bg-muted/50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-success to-accent transition-smooth" style={{ width: `${Math.min(referralStats?.conversionRate, 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Referral Link Generator */}
                <div className="bg-card rounded-lg p-24 md:p-32 shadow-elevation-2 border border-border">
                  <div className="mb-24">
                    <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-8">Your Referral Link</h2>
                    <p className="text-muted-foreground">Share this link with friends to earn bonus points</p>
                  </div>

                  <div className="mb-24 p-20 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-center justify-between mb-12">
                      <span className="text-sm font-medium text-muted-foreground">Your Referral Code</span>
                      <div className="flex items-center gap-8 px-12 py-6 bg-primary/10 rounded-lg">
                        <Icon name="Hash" size={16} color="var(--color-primary)" />
                        <span className="text-lg font-heading font-bold text-primary">{referralLink?.referralCode || 'Loading...'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-24">
                    <label className="block text-sm font-medium text-card-foreground mb-8">Shareable Link</label>
                    <div className="flex gap-12">
                      <div className="flex-1">
                        <Input type="text" value={referralLink?.referralCode ? referralService?.generateReferralUrl(referralLink?.referralCode) : ''} readOnly className="font-mono text-sm" />
                      </div>
                      <Button onClick={handleCopyLink} className="flex items-center gap-8 px-20">
                        <Icon name="Copy" size={18} />
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-12">Share via Social Media</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                      {[
                        { id: 'email', icon: 'Mail', label: 'Email', color: 'var(--color-primary)' },
                        { id: 'twitter', icon: 'Twitter', label: 'Twitter', color: '#1DA1F2' },
                        { id: 'facebook', icon: 'Facebook', label: 'Facebook', color: '#4267B2' },
                        { id: 'linkedin', icon: 'Linkedin', label: 'LinkedIn', color: '#0077B5' }
                      ]?.map(platform => (
                        <button key={platform?.id} onClick={() => handleShare?.(platform?.id)} className="flex items-center justify-center gap-8 px-16 py-12 bg-muted hover:bg-muted/80 rounded-lg transition-smooth border border-border hover:border-primary/30">
                          <Icon name={platform?.icon} size={20} color={platform?.color} />
                          <span className="text-sm font-medium text-card-foreground">{platform?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-24 pt-24 border-t border-border">
                    <div className="grid grid-cols-3 gap-16">
                      <div className="text-center">
                        <p className="text-2xl font-heading font-bold text-primary mb-4">{referralLink?.totalSignups || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Signups</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-heading font-bold text-success mb-4">{referralLink?.successfulReferrals || 0}</p>
                        <p className="text-xs text-muted-foreground">Successful</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-heading font-bold text-accent mb-4">{referralLink?.totalPointsEarned?.toLocaleString() || 0}</p>
                        <p className="text-xs text-muted-foreground">Points Earned</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Active Referrals */}
                <div>
                  <div className="mb-20">
                    <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-8">Active Referrals</h2>
                    <p className="text-muted-foreground">Track your invited friends and their progress</p>
                  </div>
                  {!referralSignups || referralSignups?.length === 0 ? (
                    <div className="bg-card rounded-lg p-32 shadow-elevation-2 border border-border">
                      <div className="text-center">
                        <Icon name="Users" size={48} className="text-muted-foreground/30 mx-auto mb-16" />
                        <p className="text-muted-foreground mb-8">No referrals yet</p>
                        <p className="text-sm text-muted-foreground">Share your referral link to start earning bonus points</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden">
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b border-border">
                            <tr>
                              <th className="px-20 py-16 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Friend Email</th>
                              <th className="px-20 py-16 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Signup Date</th>
                              <th className="px-20 py-16 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                              <th className="px-20 py-16 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Progress</th>
                              <th className="px-20 py-16 text-right text-xs font-medium text-muted-foreground uppercase tracking-wide">Points Earned</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {referralSignups?.map((referral) => {
                              const getStatusConfig = (status) => {
                                const configs = {
                                  pending: { label: 'Pending', icon: 'Clock', color: 'var(--color-warning)', bgColor: 'bg-warning/10', textColor: 'text-warning' },
                                  signed_up: { label: 'Signed Up', icon: 'UserCheck', color: 'var(--color-primary)', bgColor: 'bg-primary/10', textColor: 'text-primary' },
                                  activated: { label: 'Activated', icon: 'CheckCircle', color: 'var(--color-accent)', bgColor: 'bg-accent/10', textColor: 'text-accent' },
                                  completed: { label: 'Completed', icon: 'Trophy', color: 'var(--color-success)', bgColor: 'bg-success/10', textColor: 'text-success' }
                                };
                                return configs?.[status] || configs?.pending;
                              };
                              const statusConfig = getStatusConfig(referral?.status);
                              const maskEmail = (email) => {
                                if (!email) return '';
                                const [username, domain] = email?.split('@');
                                if (username?.length <= 3) return `${username?.[0]}***@${domain}`;
                                return `${username?.slice(0, 3)}***@${domain}`;
                              };
                              return (
                                <tr key={referral?.id} className="hover:bg-muted/30 transition-smooth">
                                  <td className="px-20 py-16">
                                    <div className="flex items-center gap-12">
                                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
                                        <Icon name="User" size={18} color="var(--color-primary)" />
                                      </div>
                                      <span className="text-sm font-medium text-card-foreground">{maskEmail(referral?.referredEmail)}</span>
                                    </div>
                                  </td>
                                  <td className="px-20 py-16">
                                    <span className="text-sm text-muted-foreground">{new Date(referral?.signupDate || referral?.createdAt)?.toLocaleDateString()}</span>
                                  </td>
                                  <td className="px-20 py-16">
                                    <span className={`inline-flex items-center gap-6 px-10 py-6 rounded-lg text-xs font-medium ${statusConfig?.bgColor} ${statusConfig?.textColor}`}>
                                      <Icon name={statusConfig?.icon} size={14} color={statusConfig?.color} />
                                      {statusConfig?.label}
                                    </span>
                                  </td>
                                  <td className="px-20 py-16">
                                    <div className="flex items-center gap-8">
                                      <Icon name="CheckCircle" size={16} color={referral?.signupBonusAwarded ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
                                      <Icon name="CheckCircle" size={16} color={referral?.firstTransactionBonusAwarded ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
                                      <Icon name="CheckCircle" size={16} color={referral?.retentionBonusAwarded ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
                                    </div>
                                  </td>
                                  <td className="px-20 py-16 text-right">
                                    <div className="flex items-center justify-end gap-6">
                                      <Icon name="Coins" size={16} color="var(--color-primary)" />
                                      <span className="text-sm font-semibold text-primary">+{referral?.totalBonusPoints?.toLocaleString() || 0}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bonus Structure */}
                <div>
                  <div className="mb-20">
                    <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-8">Referral Bonus Structure</h2>
                    <p className="text-muted-foreground">Earn points at each stage of your friend's journey</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
                    {bonusStructure?.map(bonus => (
                      <div key={bonus?.id} className="bg-card rounded-lg p-20 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth border border-border">
                        <div className="flex items-start justify-between mb-16">
                          <div className="flex items-center justify-center w-12 h-12 rounded-lg" style={{ backgroundColor: `${bonus?.color}15` }}>
                            <Icon name={bonus?.icon} size={24} color={bonus?.color} strokeWidth={2.5} />
                          </div>
                          <div className="flex items-center gap-6 px-12 py-6 bg-primary/10 rounded-lg">
                            <Icon name="Coins" size={16} color="var(--color-primary)" />
                            <span className="text-sm font-semibold text-primary">+{bonus?.points?.toLocaleString()}</span>
                          </div>
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-8">{bonus?.title}</h3>
                        <p className="text-sm text-muted-foreground">{bonus?.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestone Bonuses */}
                <div>
                  <div className="mb-20">
                    <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-8">Milestone Bonuses</h2>
                    <p className="text-muted-foreground">Unlock special rewards for reaching referral milestones</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
                    {milestoneBonuses?.map(milestone => (
                      <div key={milestone?.id} className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                        <div className="flex items-start justify-between mb-16">
                          <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg">
                            <Icon name={milestone?.icon} size={24} color="var(--color-warning)" strokeWidth={2.5} />
                          </div>
                          <div className="flex items-center gap-6 px-12 py-6 bg-warning/10 rounded-lg">
                            <Icon name="Coins" size={16} color="var(--color-warning)" />
                            <span className="text-sm font-semibold text-warning">+{milestone?.bonusPoints?.toLocaleString()}</span>
                          </div>
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-8">{milestone?.title}</h3>
                        <p className="text-sm text-muted-foreground">{milestone?.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {showRedeemModal && (
        <RedeemModal
          redemption={selectedRedemption}
          userPoints={userPoints?.totalPoints}
          onConfirm={handleConfirmRedeem}
          onCancel={() => {
            setShowRedeemModal(false);
            setSelectedRedemption(null);
          }}
        />
      )}
    </>
  );
};

export default RewardsCenter;